import React, { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";

import StudentList from "./lists/StudentList";
import AgentList from "./lists/AgentList";
import CourseList from "./lists/CourseList";
import EnquiryList from "./lists/EnquiryList";
import Leavelist from "./lists/LeaveList";

import StudentRecordForm from "./forms/StudentForms/StudentRecordForm";
import AgentInternalForm from "./forms/AgentInternalForm";
import CourseForm from "./forms/CourseForm";
import ViewEnrolmentForm from "./forms/EnrolmentForms/ViewEnrolmentForm";
import EnrolmentList from "./lists/EnrolmentList";
import EnrolmentForm from "./forms/EnrolmentForms/EnrolmentForm";
import EnquiryForm from "./forms/EnquiryForm";
import DashboardReport from "./DashboardReport";
import LeaveForm from "./forms/LeaveForm";
import ViewStudentRecordForm from "./forms/StudentForms/ViewStudentRecordForm";
import { View } from "lucide-react";
import { getNewEnquiryCount } from "../services/EnquiryService"
import { getPendingLeaveCount } from "../services/LeaveService";
import { getNewEnrolmentCount, getAllStudents } from "../services/StudentRecordService";
import { getNewAgents } from "../services/AgentService";
import ViewAgentApplicationForm from "../components/forms/AgentForms/ViewAgentApplicationForm";
import InvoiceSettings from "./InvoiceSettings";
import AttendanceSection from "./attendance/AttendanceSection";

function Dashboard({ user, onLogout }) {
  const [activeSection, setActiveSection] = useState("dashboard"); // default section
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showNewForm, setNewShowForm] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // state for bubble
  const [pendingCount, setPendingCount] = useState(0);
  const [enquiryCount, setEnquiryCount] = useState(0);
  const [leaveCount, setLeaveCount] = useState(0);
  const [pendingEnrolments, setPendingEnrolments] = useState(0);
  const [prismDue, setPrismDue] = useState(0);
  const [agentNewApplication, setAgentNewApplication] = useState(0);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleNavClick = (section) => {
    setActiveSection(section);
    setShowForm(false);
    setNewShowForm(false);
    setSelectedRecord(null);
    setSidebarOpen(false);
  };

  const sections = {
    dashboard: {
      title: "",
      subtitle: "",
    },
    "enrolment-record": {
      title: "New Enrolments",
      subtitle: "View and manage enrolments",
      ListComponent: EnrolmentList,
      FormComponent: ViewEnrolmentForm,
      NewFormComponent: EnrolmentForm,
    },
    "student-record": {
      title: "Student Record",
      subtitle: "Create and manage student records",
      ListComponent: StudentList,
      FormComponent: ViewStudentRecordForm,
      NewFormComponent: StudentRecordForm,
    },
    "agent-record": {
      title: "Agent Record",
      subtitle: "Create and manage agent records",
      ListComponent: AgentList,
      FormComponent: ViewAgentApplicationForm,
      NewFormComponent: AgentInternalForm,
    },
    course: {
      title: "Course Management",
      subtitle: "Create and manage courses",
      ListComponent: CourseList,
      FormComponent: CourseForm,
      NewFormComponent: CourseForm,
    },
    enquiry: {
      title: "Student Equiry Management",
      subtitle: "Create and manage enquiries",
      ListComponent: EnquiryList,
      FormComponent: EnquiryForm,
      NewFormComponent: EnquiryForm,
    },
    leave: {
      title: "Leave Management",
      subtitle: "Manage leaves for students",
      ListComponent: Leavelist,
      FormComponent: LeaveForm,
      NewFormComponent: null,
    },
    "invoice-settings": {
      title: "Invoice Settings",
      subtitle: "Manage student sync and invoice tracking",
      Component: InvoiceSettings,
    },
    "attendance": {
      title: "Attendance",
      subtitle: "Download weekly attendance sheets by campus",
      Component: AttendanceSection,
    },
  };
  const handleStudentSaveSuccess = (savedStudent) => {
    setSelectedRecord(savedStudent);

    // Stop showing New Student form
    setNewShowForm(false);

    // Keep record view open
    setShowForm(true);

    fetchCounts();
  };

  const handleCreateNew = () => {
    setSelectedRecord(null);
    setShowForm(true);
    setNewShowForm(true);
  };

  const handleSelectRecord = (record) => {
    setSelectedRecord(record);
    setShowForm(true);
    setNewShowForm(false);
  };
  const getPrismDueSoon = async () => {
    const today = new Date();

    // Always await here to ensure you get actual data, not a Promise
    const allStudents = await getAllStudents();

    // Ensure we have an array
    if (!Array.isArray(allStudents)) {
      console.warn("⚠️ getAllStudents did not return an array:", allStudents);
      return [];
    }

    const dueSoonList = [];

    for (const student of allStudents) {
      const courses = student?.formState?.courses || [];

      for (const course of courses) {
        if (!course?.prismsDueDate) continue;

        const status = (course.prismStatus || "").trim();
        if (status !== "" && status !== "Pending" && status !== "Reported") continue;

        const dueDate = new Date(course.prismsDueDate);
        const diffDays = (dueDate - today) / (1000 * 60 * 60 * 24);

        // Check due within next 6 days (0–6)
        if (diffDays >= 0 && diffDays <= 6) {
          const diffRounded = Math.round(diffDays);

          dueSoonList.push({
            id: student.id || student.enrolmentReferenceId || "",
            studentId: student.formState?.studentId || "",
            studentName: `${student.formState?.firstName || ""} ${student.formState?.lastName || ""}`.trim(),
            studentDateOfBirth: student.formState?.dateOfBirth || "",
            courseName: course.courseName || "",
            courseStartDate: course.courseStartDate || "",
            coeNo: course.coeNumber || "",
            coeIssuedDate: course.coeIssuedDate || "",
            prismsDueDate: `${course.prismsDueDate} (${diffRounded} days)`,
            prismStatus: course.prismStatus || "",
          });
        }
      }
    }

    return dueSoonList;
  };

  
  const fetchCounts = async () => {
      try {
        const [ enrolments, enquiries, prismData, leaves, agents] = await Promise.all([
          getNewEnrolmentCount(),
          getNewEnquiryCount(),
          getPrismDueSoon(),
          getPendingLeaveCount(),
          getNewAgents(),
        ]);
        setPendingEnrolments(enrolments);
        setEnquiryCount(enquiries);
        setPrismDue(prismData.length);
        setLeaveCount(leaves);
        setAgentNewApplication(agents);
      } catch (err) {
        console.error("Error fetching counts:", err);
      }
  };
  const handleCancelForm = () => {
    setShowForm(false);
    setSelectedRecord(null);
    setNewShowForm(false);
    fetchCounts();
  };
  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 60000);
    return () => clearInterval(interval);
  }, []);
  


  const renderContent = () => {
    const config = sections[activeSection];
    if (!config) return null;

    const { title, subtitle, ListComponent, FormComponent, NewFormComponent } =
      config;
    if (activeSection === "invoice-settings") {
      return <InvoiceSettings />;
    }
    if (activeSection === "attendance") {
      return <AttendanceSection />;
    }

    // Determine the correct selection callback prop name
    const onSelectPropName =
      activeSection === "student-record" || activeSection === "enrolment-record"
        ? "onSelectStudent"
        : activeSection === "agent-record"
        ? "onSelectAgent"
        : activeSection === "course"
        ? "onSelectCourse"
        : activeSection === "enquiry"
        ? "onSelectEnquiry"
        : activeSection === "leave"
        ? "onSelectLeave"
        : "onSelectRecord";

    // Pass down pendingCount updater only for enrolments
    const listProps = {
      [onSelectPropName]: handleSelectRecord,
      ...(activeSection === "enrolment-record" && {
        onPendingCountChange: setPendingCount,
      }),
    };

    {
      if (activeSection === "dashboard") {
        return (
          <div>
            <h1 className="page-title">{title}</h1>
            <p className="page-subtitle">{subtitle}</p>
            <DashboardReport />
          </div>
        );
      } else {
        return (
          <>
            {!showForm && (
              <div
                className="page-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1rem",
                  flexWrap: "wrap",
                  gap: "1rem",
                }}
              >
                <div>
                  <h1 className="page-title">{title}</h1>
                  <p className="page-subtitle">{subtitle}</p>
                </div>
                {activeSection !== "leave" && (
                  <button
                    className="btn btn-primary"
                    onClick={handleCreateNew}
                    style={{ height: "40px" }}
                  >
                    + Create New
                  </button>
                )}
              </div>
            )}

            {activeSection === "enquiry" && showNewForm ? (
              <EnquiryForm onCancel={handleCancelForm} showSubmit={true}/>
            ) : activeSection === "leave" && showNewForm ? (
              <LeaveForm onCancel={handleCancelForm} showSubmit={true}/>
            ) : activeSection !== "enquiry" && showNewForm ? (
              <NewFormComponent onCancel={handleCancelForm} onSaveSuccess={
                activeSection === "student-record"
                  ? handleStudentSaveSuccess
                  : undefined
              }/>
            ) : showForm ? (
              <FormComponent onCancel={handleCancelForm} initialData={selectedRecord} />
            ) : (
              <ListComponent {...listProps} />
            )}
          </>
        );
      }
    }
  };

  return (
    <div className="app-container">
      <div
        className={`overlay ${sidebarOpen ? "active" : ""}`}
        onClick={toggleSidebar}
      ></div>

      <Sidebar
        activeSection={activeSection}
        onNavClick={handleNavClick}
        isOpen={sidebarOpen}
        badges={{
          "dashboard": prismDue + enquiryCount + pendingCount,
          "enrolment-record": pendingCount,
          "student-record": pendingEnrolments,
          "agent-record": agentNewApplication,
          "course": 0,
          "enquiry": enquiryCount,
          "leave": leaveCount,
          "invoice-settings": 0,
        }}
      />


      <div className={`main-content ${sidebarOpen ? "sidebar-open" : ""}`}>
        <TopBar
          user={user}
          onLogout={onLogout}
          onToggleSidebar={toggleSidebar}
          sidebarOpen={sidebarOpen}
        />

        <div className="content-area">{renderContent()}</div>
      </div>
    </div>
  );
}

export default Dashboard;
